import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findAllMatches } from "../../utils/searchUtils";

export const useMessageSearch = (messages) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const messageRefsRef = useRef({});

  const matches = useMemo(
    () => findAllMatches(messages, searchValue),
    [messages, searchValue]
  );

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchValue]);

  const handleSearchClick = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((valueOrEvent) => {
    const value = valueOrEvent?.target?.value ?? (typeof valueOrEvent === "string" ? valueOrEvent : "");
    setSearchValue(value);
  }, []);

  const handleChevronDown = useCallback(() => {
    setCurrentMatchIndex((prev) =>
      matches.length ? Math.min(prev + 1, matches.length - 1) : 0
    );
  }, [matches.length]);

  const handleChevronUp = useCallback(() => {
    setCurrentMatchIndex((prev) =>
      matches.length ? Math.max(prev - 1, 0) : 0
    );
  }, [matches.length]);

  const setMessageRef = useCallback((index) => (el) => {
    messageRefsRef.current[`${index}`] = el;
  }, []);

  const scrollToCurrentMatch = useCallback(() => {
    if (matches.length === 0) return;
    const current = matches[currentMatchIndex];
    const el = messageRefsRef.current[`${current.messageIndex}`];
    if (!el) return;

    // Prefer scrolling to the active highlighted token instead of the whole message.
    // This keeps navigation accurate for long messages with many matches.
    const targetEl = el.querySelector(".highlight-current") || el;

    const scrollParent = el.closest("[data-messages-list]");
    if (!scrollParent) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const parentRect = scrollParent.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const viewportPadding = 24;
    const isAbove = targetRect.top < parentRect.top + viewportPadding;
    const isBelow = targetRect.bottom > parentRect.bottom - viewportPadding;
    if (!isAbove && !isBelow) return;
    const maxScrollTop = scrollParent.scrollHeight - scrollParent.clientHeight;
    const targetCenter =
      targetRect.top - parentRect.top + scrollParent.scrollTop + targetRect.height / 2;
    const targetScrollTop = Math.min(
      Math.max(0, targetCenter - parentRect.height / 2),
      maxScrollTop
    );
    scrollParent.scrollTo({ top: targetScrollTop, behavior: "smooth" });
  }, [matches, currentMatchIndex]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      scrollToCurrentMatch();
    });
    return () => cancelAnimationFrame(raf);
  }, [currentMatchIndex, matches.length, scrollToCurrentMatch]);

  return {
    isSearchOpen,
    searchValue,
    currentMatchIndex,
    matches,
    handleSearchClick,
    handleCloseSearch,
    handleSearchChange,
    handleChevronDown,
    handleChevronUp,
    setMessageRef,
  };
};
